import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import AnalyticsTable from "./AnalyticsTable";
import OrgAnnouncementBanner from "./OrgAnnouncementBanner";
import { Key } from "../constants/enums";
import {
  useAccounts,
  accountName,
  accessibleTeacherUserIds,
  teacherRequestState,
} from "@/hooks/useAccounts";
import TeacherFilter from "./filters/TeacherFilter";
import { isDemoAccount, loadDemoSessions } from "../service/demoAnalytics";
import { columnConfig } from "../constants/config/columns";
import { buildAttemptId } from "../utils/attemptId";
import {
  keys,
  supportKeys,
  defaultFilterValues,
  defaultSortPreset,
} from "../constants/lists";
import {
  formatMs,
  calculateLessonsCompleted,
  calculateLessonsStarted,
  calculateTotalLessons,
  calculateTotalStudents,
  calculateAverageTimeSpent,
  calculateCompletionRate,
} from "../service/stats";
import {
  displayStateStyleMap,
  LAST_LEARNED_SWATCH,
} from "../constants/config/support";
import { SortProvider } from "../models/SortContext";
import { groupData, paginateGroups } from "../service/group";
import { applyFilters, normalizeFilterValues } from "../service/filter";
import { sortItems } from "../service/sort";
import {
  parseFiltersFromQueryParams,
  updateFilterInQueryParams,
  serializeFiltersToQueryParams,
  hasAnyFilterParams,
  getPageSizeFromQueryParams,
  setPageSizeInQueryParams,
  getPageFromQueryParams,
  setPageInQueryParams,
  getTeacherIdsFromQueryParams,
  setTeacherIdsInQueryParams,
  getDisplayNameHints,
  resolveRangeParam,
  PAGE_SIZE_OPTIONS,
} from "../service/queryParams";
import HeadingBlock from "./HeadingBlock";
import Item from "./scrollable/Item";
import { BiteStatus, BiteDisplayState } from "../types/enums";
import Card from "./overview/Card";
import FilterDropdown from "./filters/FilterDropdown";
import {
  filterConfigs,
  labelByValue,
  type AppliedFilters,
} from "../constants/config/filter";
import { ANALYTICS_PATH } from "../service/deepLink";
import InfoTooltip from "./InfoTooltip";
import { CaretPosition } from "../../../types/ui";
import type { SortPreset } from "../constants/config/sort";
import api from "../../../utils/axios-utils";
import { useAuth } from "../../../hooks/useAuth";
import { useAuthStore } from "@/store/useAuthStore";
import { hasRole } from "@/utils/role-utils";
import type {
  AnalyticsData,
  ExitTicketAttemptDetail,
  ExitTicketData,
} from "../types/data";
import {
  useAnalyticsEventStream,
  type AnalyticsBiteUpdatedPayload,
} from "../hooks/analyticsEventStreamHook";
import {
  mergeSessionRows,
  patchBiteProcessing,
  processingSessionIds,
  resolvedProcessingBites,
} from "../service/liveUpdates";
import { LessonStatus } from "../constants/enums";
import AnalyticsPagination from "./AnalyticsPagination";
import SupportTranscriptsModal from "./modals/SupportTranscriptsModal";
import ExitTicketDetailModal from "./modals/ExitTicketDetailModal";
import { useSupportModalStore } from "../stores/supportModalStore";
import { useBiteAttemptsStore } from "../stores/biteAttemptsStore";
import { useTranscriptionsEnabled } from "@/hooks/useRuntimeFlags";

// Backoff for the processing poll. Analyses land within seconds, so the early
// polls do the real work; after a minute of no change, asking at the same rate
// is waste. With no timeout bounding the wait, this is what keeps a bite whose
// analysis never lands from polling forever.
const POLL_DELAYS_MS = [7_000, 15_000, 30_000, 60_000];

interface AnalyticsViewContentProps {
  groupBy?: Key;
}

type OverviewStats = {
  totalStudents: number;
  totalLessons: number;
  lessonsStarted: number;
  lessonsCompleted: number;
  averageTimeSpentMs: number;
  completionRate: number;
};

const AnalyticsViewContent = ({
  groupBy = Key.STUDENT_NAME,
}: AnalyticsViewContentProps) => {
  const transcriptionsEnabled = useTranscriptionsEnabled();
  const { user } = useAuth();
  const {
    accounts,
    isLoading: isLoadingAccounts,
    error: accountsError,
  } = useAccounts();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Dashboard teacher selector (org admin / multi-account co-teacher). Drives
  // the analytics user_ids; empty means "view the acting teacher's students".
  const [isTeacherFilterOpen, setIsTeacherFilterOpen] = useState(false);
  const nameHints = useMemo(
    () => getDisplayNameHints(searchParams),
    [searchParams],
  );

  const requestedTeacherIds = useMemo(
    () => getTeacherIdsFromQueryParams(searchParams),
    [searchParams],
  );

  // Filtered on accessibility, not on `has_analytics`: a teacher the viewer may
  // see but who has no sessions yet must still scope the view to themselves and
  // come back empty. Dropping them here would fall through to defaultUserIds and
  // show the viewer's own students under the requested teacher's name.
  const selectedUserIds = useMemo(() => {
    if (requestedTeacherIds.length === 0) return [];
    const accessible = accessibleTeacherUserIds(accounts);
    return requestedTeacherIds.filter((id) => accessible.has(id));
  }, [requestedTeacherIds, accounts]);

  // Whether the URL's teacherId can be honoured. Anything other than "none" or
  // "granted" means the link asked for a scope this view cannot currently show,
  // and the default scope below must not stand in for it.
  const teacherRequest = useMemo(
    () =>
      teacherRequestState(
        requestedTeacherIds,
        accounts,
        isLoadingAccounts,
        accountsError,
      ),
    [requestedTeacherIds, accounts, isLoadingAccounts, accountsError],
  );

  // The fetch was deliberately never made in these two states, so there is no
  // result to report — only a reason. Everything downstream of the banner is
  // suppressed rather than rendered from the empty `data`, which would
  // otherwise show a zero-value Overview and blame the date range for what is
  // actually an access problem.
  const isTeacherScopeBlocked =
    teacherRequest === "unresolvable" || teacherRequest === "denied";

  const setSelectedUserIds = (userIds: number[]) => {
    let next = setTeacherIdsInQueryParams(userIds, searchParams);
    next = setPageInQueryParams(1, next);
    setSearchParams(next);
  };

  // The default scope follows the account switcher: when viewing-as another
  // teacher, analytics defaults to THAT teacher rather than the logged-in user.
  // The teacher filter overrides this for co-teachers / any org teacher.
  // For an org admin who hasn't switched into a teacher (and so has no own
  // teacher row), default to every accessible teacher — the aggregate org view.
  const actingTeacherId = useAuthStore((s) => s.actingTeacherId);
  const greetingAccount = accounts.find(
    (account) => account.teacher_id === actingTeacherId,
  );
  const greetingName = greetingAccount
    ? accountName(greetingAccount)
    : user?.name || user?.username || "there";
  const isOrgAdmin = hasRole(user?.role, "ORG_ADMIN");
  const defaultUserIds = useMemo(() => {
    if (isOrgAdmin) {
      const teacherUserIds = accounts
        .filter((a) => !a.is_you)
        .map((a) => a.user_id);
      return teacherUserIds;
    }
    const actingAccount =
      actingTeacherId !== null
        ? accounts.find((a) => a.teacher_id === actingTeacherId)
        : accounts.find((a) => a.is_you);
    if (actingAccount) return [actingAccount.user_id];
    if (actingTeacherId === null && accounts.length > 0) {
      return accounts.map((a) => a.user_id);
    }
    return user?.id != null ? [user.id] : [];
  }, [isOrgAdmin, actingTeacherId, accounts, user?.id]);

  const analyticsUserIds =
    selectedUserIds.length > 0 ? selectedUserIds : defaultUserIds;
  const analyticsUserIdsKey = analyticsUserIds.join(",");

  const [data, setData] = useState<AnalyticsData[]>([]);
  const [overview, setOverview] = useState<OverviewStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Bumping this re-runs the fetch effect below — the fetch lives inside it and
  // cannot be called directly. The value is never read.
  const [refreshNonce, setRefreshNonce] = useState(0);
  // Set by the poll timer and the visibility/reconnect handlers, consumed once at
  // the top of the fetch effect. A background refresh must not show the spinners.
  const isBackgroundRefresh = useRef(false);
  // Which sessions the pending refresh is for; null means "everything".
  // Read once by the fetch effect, alongside isBackgroundRefresh.
  const scopedSessionIdsRef = useRef<string[] | null>(null);
  const pollDelayRef = useRef(POLL_DELAYS_MS[0]);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The rows as they currently stand, readable outside a render. The SSE handler
  // needs them to build its patch, and the fetch needs them on its error path —
  // neither can wait for a re-render to see the latest.
  const dataRef = useRef<AnalyticsData[]>(data);
  dataRef.current = data;

  // Which kind of fetch is in flight, or false when none is. Lets the SSE
  // handler know its update might get raced by a fetch that queried the
  // database before this event landed — and lets the poll timer defer
  // instead of requesting a scoped refresh that would re-trigger the fetch
  // effect and cancel a full refresh already running (the full refresh's own
  // completion re-arms the poll loop anyway, with fresher, more complete
  // data than the scoped poll would have had).
  const fetchInFlightRef = useRef<"full" | "scoped" | false>(false);
  // Bites the SSE handler patched in while a fetch was in flight. Replayed
  // once that fetch's response is applied, so its (possibly stale) answer for
  // these specific bites can't silently erase what we just learned.
  const pendingSseReplayRef = useRef<{ sessionId: string; slideIdx: number }[]>(
    [],
  );

  const requestRefresh = useCallback(
    (background: boolean, sessionIds?: string[]) => {
      isBackgroundRefresh.current = background;
      scopedSessionIdsRef.current =
        sessionIds && sessionIds.length > 0 ? sessionIds : null;
      setRefreshNonce((n) => n + 1);
    },
    [],
  );

  // A full re-read. Used wherever we must assume our view has gone stale rather
  // than merely incomplete: mount, returning to the tab, and SSE reconnect.
  const requestFullRefresh = useCallback(
    (background: boolean) => {
      pollDelayRef.current = POLL_DELAYS_MS[0];
      requestRefresh(background);
    },
    [requestRefresh],
  );

  // THE one place that decides whether the dashboard keeps asking.
  //
  // Called from exactly two places, because exactly two things can change what is
  // pending: an SSE patch, and a finished fetch. Both hand it the rows they just
  // produced, so the decision is always made against what is actually on screen.
  //
  // Does NOT cancel and restart an already-running timer: a burst of SSE events
  // arriving faster than the current delay would otherwise reset the countdown
  // every time and the poll would never fire at all. Only arms a fresh timer
  // when none is running; an already-armed timer is left alone and recomputes
  // the pending set fresh when it fires, so it still picks up anything new.
  const scheduleNextPoll = useCallback(
    (rows: AnalyticsData[]) => {
      // A hidden tab stops asking. Becoming visible triggers a full refresh,
      // whose fetch calls back in here and restarts the loop.
      if (document.hidden) {
        if (pollTimerRef.current !== null) {
          clearTimeout(pollTimerRef.current);
          pollTimerRef.current = null;
        }
        return;
      }

      const pendingSessionIds = processingSessionIds(rows);
      if (pendingSessionIds.length === 0) {
        if (pollTimerRef.current !== null) {
          clearTimeout(pollTimerRef.current);
          pollTimerRef.current = null;
        }
        pollDelayRef.current = POLL_DELAYS_MS[0];
        return;
      }

      // A timer is already counting down toward the same decision. Restarting
      // it here on every call — as this used to do — meant a steady stream of
      // SSE events (a new bite every few seconds) could keep resetting the
      // wait forever, and the poll would never actually fire. Let it finish;
      // it recomputes the pending set fresh when it fires, so anything new
      // that arrived in the meantime is still covered.
      if (pollTimerRef.current !== null) return;

      const delay = pollDelayRef.current;
      pollTimerRef.current = setTimeout(() => {
        pollTimerRef.current = null;
        if (fetchInFlightRef.current === "full") {
          // A full refresh already covers everything this poll would have
          // asked about, and re-arms the poll loop itself once it lands
          // (finally calls scheduleNextPoll below). Requesting a scoped
          // refresh here would re-trigger the fetch effect and cancel that
          // full refresh mid-flight — defer instead.
          return;
        }
        // Advance the rung when the poll fires, not when it is scheduled — this
        // function runs on every patch and every fetch, and advancing here would
        // climb the ladder on activity rather than on waiting.
        pollDelayRef.current =
          POLL_DELAYS_MS[
            Math.min(
              POLL_DELAYS_MS.indexOf(delay) + 1,
              POLL_DELAYS_MS.length - 1,
            )
          ];
        requestRefresh(true, processingSessionIds(dataRef.current));
      }, delay);
    },
    [requestRefresh],
  );

  useEffect(
    () => () => {
      if (pollTimerRef.current !== null) clearTimeout(pollTimerRef.current);
    },
    [],
  );

  // The event carries no status, so this only starts the pulse. Everything
  // status-bearing — bite status, lesson status, and therefore every overview
  // card — arrives on the scoped poll this sets off.
  const handleBiteUpdated = useCallback(
    ({ sessionId, slideIdx }: AnalyticsBiteUpdatedPayload) => {
      // Queued unconditionally, before even trying to patch — a fetch already
      // in flight queried the database before this event landed, so its answer
      // for this bite may be stale by the time it resolves, whether or not
      // there's anything to patch *yet*. A session/slide the grid hasn't
      // loaded (e.g. the mount fetch hasn't landed) is exactly the case where
      // patching below is a no-op, and the row only exists once that fetch's
      // own result arrives — the replay in `finally` re-applies this against
      // that fresh result, so it must survive being queued from here even
      // when there was nothing yet to patch.
      if (fetchInFlightRef.current) {
        pendingSseReplayRef.current.push({ sessionId, slideIdx });
      }
      // Patched off the ref rather than inside a setData updater: the updater can
      // run more than once (StrictMode), and scheduling from inside it would be a
      // side effect in a place React is free to replay. Writing the ref back
      // immediately also lets two events in the same tick chain correctly.
      const next = patchBiteProcessing(dataRef.current, sessionId, slideIdx);
      if (next === dataRef.current) return;
      dataRef.current = next;
      setData(next);
      scheduleNextPoll(next);
    },
    [scheduleNextPoll],
  );

  // onOpen fires on the first connect as well as reconnects, and the mount fetch
  // has already covered that one. Skipping it avoids a duplicate request on every
  // page load.
  const sseHasConnected = useRef(false);
  const handleSseOpen = useCallback(() => {
    if (!sseHasConnected.current) {
      sseHasConnected.current = true;
      return;
    }
    // The connection was down. Anything published in the gap is gone — SSE has
    // no replay — so a tile may be silently missing its Processing flag, and the
    // scoped poll can only ask about tiles it already knows about.
    requestFullRefresh(true);
  }, [requestFullRefresh]);

  useAnalyticsEventStream(true, {
    onAnalyticsBiteUpdated: handleBiteUpdated,
    onOpen: handleSseOpen,
  });

  const supportModal = useSupportModalStore();

  const [detailModal, setDetailModal] = useState<{
    attemptId: number;
    studentName: string;
    assessmentLabel: string;
  } | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const { attemptId, studentName, assessmentLabel } = (e as CustomEvent)
        .detail;
      window.dispatchEvent(new CustomEvent("close-all-tooltips"));
      setDetailModal({ attemptId, studentName, assessmentLabel });
    };
    window.addEventListener("open-attempt-detail", handler);
    return () => window.removeEventListener("open-attempt-detail", handler);
  }, []);

  const appliedFilters = useMemo<AppliedFilters>(() => {
    const parsed = parseFiltersFromQueryParams(searchParams);
    const seeded =
      Object.keys(parsed).length > 0 ? parsed : defaultFilterValues;
    return normalizeFilterValues(seeded, data);
  }, [searchParams, data]);
  const [openFilterKey, setOpenFilterKey] = useState<Key | null>(null);
  const [currentSort, setCurrentSort] = useState<SortPreset | null>(
    defaultSortPreset,
  );

  useEffect(() => {
    let initialParams = new URLSearchParams(window.location.search);

    const rangeResolved = resolveRangeParam(initialParams);
    if (rangeResolved) {
      initialParams = rangeResolved;
      setSearchParams(initialParams, { replace: true });
    }

    if (!hasAnyFilterParams(initialParams)) {
      const defaultParams = serializeFiltersToQueryParams(
        defaultFilterValues,
        initialParams,
      );
      setSearchParams(defaultParams, { replace: true });
    }
    // Seed default filters once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    // Consume the flags: each run decides for itself, and a stale true would make
    // the next genuine scope change skip its spinner. Likewise a stale scope
    // would silently turn a full refresh into a partial one.
    const isBackground = isBackgroundRefresh.current;
    isBackgroundRefresh.current = false;
    const scopedIds = scopedSessionIdsRef.current;
    scopedSessionIdsRef.current = null;
    // What this run ended up putting on screen, for the scheduling decision in
    // `finally`. Null means it never got that far.
    let nextRows: AnalyticsData[] | null = null;

    const mapLessonStatus = (
      status: string | null | undefined,
    ): LessonStatus => {
      const s = (status || "").toString().toLowerCase();
      if (s === "completed" || s === "complete") return LessonStatus.COMPLETED;
      if (s === "started" || s === "in_progress" || s === "inprogress")
        return LessonStatus.STARTED;
      return LessonStatus.NOT_STARTED;
    };

    const mapBiteStatus = (status: string | null | undefined): BiteStatus => {
      if (status === BiteStatus.COMPLETED) return BiteStatus.COMPLETED;
      // Persisted by the Lambda writer until 2025-12-22 for started-but-unfinished
      // bites, so old rows still carry it. Not a value the backend can emit today.
      if (status === "currently_learning") return BiteStatus.INCOMPLETE;
      if (status === BiteStatus.INCOMPLETE) return BiteStatus.INCOMPLETE;
      // bite_status now carries the full domain; a skip is "not completed" —
      // the teacher dashboard has no distinct skip state, so treat it as
      // incomplete (matches the physical-column path, which folds skip → incomplete).
      if (
        status === BiteStatus.SKIPPED_BY_USER ||
        status === BiteStatus.SKIPPED_BY_AI
      )
        return BiteStatus.INCOMPLETE;
      return BiteStatus.NOT_STARTED;
    };

    const mapAttempt = (a: any): ExitTicketAttemptDetail => ({
      attemptId: a.attempt_id ?? 0,
      attemptNumber: a.attempt_number ?? 0,
      score: a.score ?? null,
      totalQuestions: a.total_questions ?? 0,
      correctCount: a.correct_count ?? 0,
      incorrectCount: a.incorrect_count ?? 0,
      notAttemptedCount: a.not_attempted_count ?? 0,
      startedAt: a.started_at ?? null,
      submittedAt: a.submitted_at ?? null,
      timeSpentMs: a.time_spent_ms ?? 0,
      isBest: a.is_best ?? false,
    });

    const mapAttempts = (et: any): ExitTicketAttemptDetail[] => {
      if (Array.isArray(et?.attempts) && et.attempts.length > 0)
        return et.attempts.map(mapAttempt);
      if (et?.best_attempt)
        return [mapAttempt({ ...et.best_attempt, is_best: true })];
      return [];
    };

    const deriveExitTicket = (et: any, studentName: string): ExitTicketData => {
      const base = {
        totalAttempts: et?.completed_attempts ?? 0,
        attempts: mapAttempts(et),
        studentName,
      };
      if (!et) return { ...base, status: "na", score: null, label: "N/A" };
      if (et.completed_attempts === 0 && !et.has_in_progress)
        return {
          ...base,
          status: "not_started",
          score: null,
          label: "Not Started",
        };
      if (et.completed_attempts === 0 && et.has_in_progress)
        return {
          ...base,
          status: "not_submitted",
          score: null,
          label: "Not Submitted",
        };
      const score = et.best_attempt?.score ?? 0;
      if (score === 100)
        return { ...base, status: "mastered", score, label: "Mastered" };
      if (score > 50)
        return {
          ...base,
          status: "partially_mastered",
          score,
          label: "Partially Mastered",
        };
      return { ...base, status: "not_mastered", score, label: "Not Mastered" };
    };

    const deriveCustomTicket = (
      ct: any,
      studentName: string,
    ): ExitTicketData => {
      const data = deriveExitTicket(ct, studentName);
      if (
        data.status === "mastered" ||
        data.status === "partially_mastered" ||
        data.status === "not_mastered"
      )
        // Custom pages are surveys and reflections, so mastery wording does not
        // apply. Once anything is handed in the cell states that plainly and
        // the tooltip carries the per-page detail.
        return { ...data, label: "Submitted" };
      return data;
    };

    // Normalize timestamps to local calendar day (YYYY-MM-DD)
    const toLocalDateString = (
      ts: string | number | null | undefined,
    ): string => {
      if (ts === null || ts === undefined) return dayjs().format("YYYY-MM-DD");
      const n = typeof ts === "string" ? Number(ts) : ts;
      return dayjs(n).format("YYYY-MM-DD");
    };

    const load = async () => {
      // Claim (or clear) fetchInFlightRef for this run before anything else,
      // synchronously — guaranteed to run before any earlier, now-cancelled
      // run's own `finally` can fire (JS finishes all synchronous work before
      // any pending promise continuation). That earlier run's `finally` stays
      // gated on `!cancelled` and never touches this flag once cancelled, so
      // it can neither leave it stuck (this line always claims it fresh) nor
      // clobber whatever this run sets it to next (the gate stops it).
      fetchInFlightRef.current = false;
      // A URL-requested teacher that isn't "granted" is filtered out of
      // selectedUserIds (see above), so analyticsUserIds falls back to
      // defaultUserIds — which, for a non-org-admin, falls back further to the
      // viewer's own id. Left unguarded, the page renders the viewer's own
      // students under a chip naming the requested teacher: not an empty view
      // or an error, but a different teacher's data presented as theirs.
      if (teacherRequest === "pending") {
        // Accounts are still in flight; a correct answer is still coming.
        setData([]);
        setOverview(null);
        setIsLoading(true);
        scheduleNextPoll([]);
        return;
      }
      if (teacherRequest === "unresolvable" || teacherRequest === "denied") {
        // No correct answer is coming. Stop, and let the banner below say why.
        setData([]);
        setOverview(null);
        setIsLoading(false);
        scheduleNextPoll([]);
        return;
      }
      // No resolved teachers yet (e.g. org admin whose accounts haven't loaded).
      // Sending an empty user_ids makes the backend fall back to the caller's
      // own id, which has no Teacher row for an admin → 403. Show empty instead.
      // The demo fixture is scoped to nobody, so it needs no resolved teachers.
      if (analyticsUserIds.length === 0 && !isDemoAccount("sessions")) {
        setData([]);
        setOverview(null);
        setIsLoading(isLoadingAccounts);
        scheduleNextPoll([]);
        return;
      }
      if (!isBackground) setIsLoading(true);
      fetchInFlightRef.current = scopedIds === null ? "full" : "scoped";
      try {
        // Demo accounts read a frozen capture instead — see service/demoAnalytics.
        const responseData = isDemoAccount("sessions")
          ? await loadDemoSessions()
          : (
              await api.get("/analytics/sessions", {
                params: {
                  user_id: analyticsUserIds[0] ?? null,
                  user_ids: analyticsUserIds,
                  // Omitted entirely on a full refresh. The backend scopes only the
                  // session list with this — the overview aggregate ignores it — but
                  // we merge rather than replace below either way.
                  ...(scopedIds ? { session_ids: scopedIds } : {}),
                },
                paramsSerializer: { indexes: null },
              })
            ).data || {};
        const sessions: Array<any> = Array.isArray(responseData.sessions)
          ? responseData.sessions
          : [];

        // Extract overview stats from API response
        const apiOverview = responseData.overview;
        if (apiOverview) {
          setOverview({
            totalStudents: apiOverview.total_students ?? 0,
            totalLessons: apiOverview.total_lessons ?? 0,
            lessonsStarted: apiOverview.lessons_started ?? 0,
            lessonsCompleted: apiOverview.lessons_completed ?? 0,
            averageTimeSpentMs: apiOverview.average_time_spent_ms ?? 0,
            completionRate: apiOverview.completion_rate ?? 0,
          });
        }

        const mapped: AnalyticsData[] = sessions.map((s) => {
          const bites = Array.isArray(s.bites) ? s.bites : [];
          const lessonAssignedAt =
            bites.length > 0 ? bites[0].lesson_assigned_at : null;

          // Compute the most recent learning activity date for Last Learned
          const getLastLearnedDate = (): string | null => {
            // Helper to coerce possible string/number to number
            const toNum = (v: unknown): number | null => {
              if (v === null || v === undefined) return null;
              const n = typeof v === "string" ? Number(v) : (v as number);
              return Number.isFinite(n) && n > 0 ? n : null;
            };

            // Per lambda upsert (postgres_writer.py), session_end_ts is maintained
            // as the GREATEST of observed slide_end_ts values. If present, it is
            // the authoritative "latest" activity timestamp.
            const endTs = toNum(s.session_end_ts);
            if (endTs !== null) {
              return dayjs(endTs).format("YYYY-MM-DD");
            }

            // Fallback: derive latest across bite/attempt timestamps
            let latest: number | null = null;
            for (const b of bites) {
              const topTs = toNum(
                (b as any).slide_end_ts ?? (b as any).slide_start_ts,
              );
              if (topTs !== null) {
                latest = latest === null ? topTs : Math.max(latest, topTs);
              }
              const attempts = Array.isArray((b as any).attempts)
                ? (b as any).attempts
                : [];
              for (const a of attempts) {
                const aTs = toNum(a.slide_end_ts ?? a.slide_start_ts);
                if (aTs !== null) {
                  latest = latest === null ? aTs : Math.max(latest, aTs);
                }
              }
            }
            if (latest !== null) {
              return dayjs(latest).format("YYYY-MM-DD");
            }

            // Fallback to session start (indicates the student opened the lesson)
            const startTs = toNum(s.session_start_ts);
            if (startTs !== null) {
              return dayjs(startTs).format("YYYY-MM-DD");
            }
            return null;
          };

          return {
            [Key.CLASSROOM_NAME]: {
              value: String(s.classroom_id ?? ""),
              label: s.classroom_name || "Group",
            },
            teacherName: s.teacher_name || undefined,
            [Key.LESSON_NAME]: {
              value: String(s.lesson_id ?? ""),
              label: s.lesson_name || "Lesson",
            },
            [Key.STUDENT_NAME]: {
              value: String(s.user_name ?? ""),
              label: s.user_name || "Student",
            },
            [Key.ASSIGN_DATE]: toLocalDateString(
              lessonAssignedAt ?? s.session_start_ts,
            ),
            [Key.LAST_LEARNED]: getLastLearnedDate(),
            [Key.TIME_SPENT]: Number(s.time_spent_ms ?? 0),
            [Key.BITES]: bites
              .map((b: any) => {
                const slideIdx = Number(b.slide_idx ?? 0);

                const attemptRows = Array.isArray(b.attempts) ? b.attempts : [];
                const mappedAttempts = attemptRows.map((a: any) => {
                  const attemptsTotal = Number(a.attempts_total ?? 0);
                  const attemptsIncorrect = Number(a.attempts_incorrect ?? 0);

                  return {
                    id: buildAttemptId(s.session_id, slideIdx, a.slide_end_ts),
                    startTime: Number(
                      a.slide_start_ts ??
                        a.slide_end_ts ??
                        s.session_start_ts ??
                        0,
                    ),
                    biteStatus: mapBiteStatus(a.bite_status),
                    studentPerformance:
                      attemptsTotal > 0 || Number(a.time_spent_ms ?? 0) > 0
                        ? {
                            studentAsked: Number(a.student_asked_count ?? 0),
                            AIAsked: Number(a.ai_asked_count ?? 0),
                            timeSpent: Number(a.time_spent_ms ?? 0),
                            incorrectAttempts: attemptsIncorrect,
                            totalAttempts: attemptsTotal,
                          }
                        : undefined,
                  };
                });

                return {
                  biteId: `${s.session_id}:${slideIdx}`,
                  biteIndex: slideIdx,
                  biteStatus: mapBiteStatus(b.bite_status),
                  biteType: b.bite_type ?? undefined,
                  tutorSupportLevel: b.tutor_support_level?.toLowerCase(),
                  attempts: mappedAttempts,
                  isLastLearned: s.currently_learning_slide_idx === slideIdx,
                  isProcessing: b.is_processing ?? false,
                };
              })
              .sort((a: any, b: any) => a.biteIndex - b.biteIndex)
              .map((bite: any, i: number) => ({ ...bite, biteIndex: i + 1 })),
            [Key.PRE_ASSESSMENT]: deriveExitTicket(
              s.pre_assessment,
              s.user_name || "Student",
            ),
            [Key.EXIT_TICKET]: deriveExitTicket(
              s.exit_ticket,
              s.user_name || "Student",
            ),
            [Key.CUSTOM_TICKET]: deriveCustomTicket(
              s.custom_ticket,
              s.user_name || "Student",
            ),
            [Key.LESSON_STATUS]: mapLessonStatus(s.lesson_status),
            goalsMet: Number(s.goals_met ?? 0),
            totalGoals: Number(s.total_goals ?? 0),
            lessonId: Number(s.lesson_id ?? 0),
            userId: Number(s.user_id ?? 0),
            sessionId: String(s.session_id ?? ""),
          };
        });

        if (!cancelled) {
          // A scoped fetch asked about some sessions, so it can only speak for
          // those: merge it onto what is already on screen. Replacing would drop
          // every row it did not ask about. A full fetch speaks for everything
          // and replaces. Not written to state yet — `finally` below is the one
          // place that applies a result, after folding in anything an SSE event
          // learned while this fetch was in flight.
          nextRows = scopedIds
            ? mergeSessionRows(dataRef.current, mapped)
            : mapped;
        }
      } catch (error) {
        console.error("Failed to load analytics data:", error);
        if (!cancelled && !scopedIds && !isBackground) {
          // Only a failed foreground full load empties the grid. A background
          // poll that fails must leave what is on screen alone — it is stale at
          // worst, where clearing would blank a working dashboard over one
          // dropped request, and the next poll or tab return will recover.
          nextRows = [];
        }
      } finally {
        if (!cancelled) {
          fetchInFlightRef.current = false;
          // Fold in any bite the SSE handler patched in while this fetch was in
          // flight — its own answer for those may predate that event. Applied
          // once, here, so `data` never briefly shows the stale answer before
          // being corrected: one write, already right.
          const replay = pendingSseReplayRef.current;
          pendingSseReplayRef.current = [];
          let finalRows = nextRows ?? dataRef.current;
          for (const { sessionId, slideIdx } of replay) {
            finalRows = patchBiteProcessing(finalRows, sessionId, slideIdx);
          }
          if (finalRows !== dataRef.current) {
            // A bite that just dropped out of "awaiting analysis" has a fresh
            // answer sitting in Postgres for the first time. The attempt-list
            // drawer caches its own fetch independently of this grid, so
            // nothing else would tell it that answer exists.
            for (const { sessionId, slideIdx } of resolvedProcessingBites(
              dataRef.current,
              finalRows,
            )) {
              useBiteAttemptsStore.getState().invalidate(sessionId, slideIdx);
            }
            dataRef.current = finalRows;
            setData(finalRows);
          }
          setIsLoading(false);
          // In `finally`, so a failed poll re-schedules instead of ending the
          // loop. This is the "after every fetch, decide again" half of the rule
          // — the SSE handler is the other half.
          scheduleNextPoll(finalRows);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyticsUserIdsKey, isLoadingAccounts, teacherRequest, refreshNonce]);

  // A hidden tab stops asking: a timer already running fires once, and
  // scheduleNextPoll then sees document.hidden and starts no replacement.
  // Coming back is worth one immediate fetch — time has passed and the answer may
  // already have changed — and a reset to the fastest interval, since the backoff
  // measures how long *this viewer* has been watching nothing happen.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) return;
      // Full, not scoped: while hidden we were not polling, and a bite we never
      // learned about cannot be in the scoped set.
      requestFullRefresh(true);
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [requestFullRefresh]);

  const hasExitTicket = useMemo(() => {
    const seenLessons = new Set<string>();
    return data.some((d) => {
      const lessonId = d[Key.LESSON_NAME].value;
      if (seenLessons.has(lessonId)) return false;
      seenLessons.add(lessonId);
      return d[Key.EXIT_TICKET].status !== "na";
    });
  }, [data]);
  const showMastery = isLoading || hasExitTicket;

  const displayHeaders = keys
    .filter((key): key is Key => columnConfig[key] !== undefined)
    .filter((key) => key !== Key.EXIT_TICKET || hasExitTicket)
    .map((key) => ({
      key,
      label: columnConfig[key]!.label,
      info: columnConfig[key]!.info,
    }));

  const filteredData = useMemo(() => {
    const result = applyFilters(data, appliedFilters);
    return result;
  }, [appliedFilters, data]);

  // Only speaks up when the group filter yields nothing at all; a partly-matching
  // multi-selection still has rows to show and needs no banner.
  const groupFilterDiagnosis = useMemo(() => {
    const selected = appliedFilters[Key.CLASSROOM_NAME];
    if (!selected?.length || isLoading || filteredData.length > 0) return null;

    const known = labelByValue(data, Key.CLASSROOM_NAME);
    const unmatched = selected.filter((value) => !known.has(value));
    if (unmatched.length === 0) return null;
    // A value that is not even shaped like an id cannot name a real group.
    return unmatched.some((value) => !/^\d+$/.test(value))
      ? ("malformed" as const)
      : ("unresolved" as const);
  }, [appliedFilters, data, isLoading, filteredData]);

  const sortedData = useMemo(() => {
    if (!currentSort) return filteredData;
    return sortItems(filteredData, {
      accessor: currentSort.accessor,
      direction: currentSort.direction,
    });
  }, [filteredData, currentSort]);

  // Metrics derived from currently filtered data (for accurate ratios)
  const filteredTotals = useMemo(() => {
    const mastered = filteredData.filter(
      (d) => d[Key.EXIT_TICKET].status === "mastered",
    ).length;
    const notMastered = filteredData.filter((d) => {
      const s = d[Key.EXIT_TICKET].status;
      return s === "not_mastered" || s === "partially_mastered";
    }).length;
    return {
      totalStudents: calculateTotalStudents(filteredData),
      totalLessons: calculateTotalLessons(filteredData),
      lessonsStarted: calculateLessonsStarted(filteredData),
      lessonsCompleted: calculateLessonsCompleted(filteredData),
      averageTimeSpentMs: calculateAverageTimeSpent(filteredData),
      completionRate: calculateCompletionRate(filteredData),
      mastered,
      notMastered,
    };
  }, [filteredData]);

  const groups = useMemo(
    () => groupData(sortedData, groupBy),
    [sortedData, groupBy],
  );

  // Paginate by group: each page holds a fixed number of groups (students),
  // regardless of how many rows each group contains.
  const pageSizeGroups = useMemo(
    () => getPageSizeFromQueryParams(searchParams),
    [searchParams],
  );
  const currentPage = useMemo(
    () => getPageFromQueryParams(searchParams),
    [searchParams],
  );
  const pages = useMemo(
    () => paginateGroups(groups, pageSizeGroups),
    [groups, pageSizeGroups],
  );
  const totalPages = pages.length;

  const goToPage = (page: number) => {
    setSearchParams(setPageInQueryParams(page, searchParams), {
      replace: true,
    });
  };

  const handlePageSizeChange = (nextPageSizeGroups: number) => {
    let next = setPageSizeInQueryParams(nextPageSizeGroups, searchParams);
    next = setPageInQueryParams(1, next);
    setSearchParams(next);
  };

  // Clamp an out-of-range page (e.g. a shared ?page=99 or filters shrinking the
  // result set) back to the last valid page.
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setSearchParams(setPageInQueryParams(totalPages, searchParams), {
        replace: true,
      });
    }
  }, [currentPage, totalPages, searchParams, setSearchParams]);

  const visibleGroups = pages[currentPage - 1] ?? [];

  const handleFilterChange = (
    key: keyof AppliedFilters,
    value: AppliedFilters[keyof AppliedFilters] | undefined,
  ) => {
    // Single-key update fallback; reset to page 1 since the result set changes
    let next = updateFilterInQueryParams(key, value, searchParams);
    next = setPageInQueryParams(1, next);
    setSearchParams(next);
  };

  // Batch apply multiple filter changes in a single URL update
  const handleBatchFilterChange = (
    updates: Partial<AppliedFilters> & { includeNotStarted?: boolean },
  ) => {
    let next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([k, v]) => {
      next = updateFilterInQueryParams(
        k as keyof AppliedFilters,
        v as any,
        next,
      );
    });
    next = setPageInQueryParams(1, next);
    setSearchParams(next);
  };

  const handleFilterToggle = (key: Key) => {
    setIsTeacherFilterOpen(false);
    setOpenFilterKey((prev) => (prev === key ? null : key));
  };

  const handleTeacherFilterToggle = () => {
    setOpenFilterKey(null);
    setIsTeacherFilterOpen((v) => !v);
  };

  const handleSortSelect = (sort: SortPreset | null) => {
    setCurrentSort(sort);
    goToPage(1);
  };

  return (
    <div className="et-analytics-view">
      <header className="et-analytics-greeting">
        <h1>Hey {greetingName.split(" ")[0]},</h1>
      </header>
      <SortProvider currentSort={currentSort} onSelect={handleSortSelect}>
        <div
          className="et-analytics-filters"
          role="group"
          aria-label="Filter analytics"
        >
          <div className="sr-only">Filter data by:</div>
          <div className="flex flex-row flex-wrap items-center gap-3">
            {/* Hidden for the demo: the frozen capture is a single scope, so
                  picking a teacher would rewrite the URL and change nothing. */}
            {accounts.length > 1 && !isDemoAccount("sessions") && (
              <div className="w-auto">
                <TeacherFilter
                  accounts={accounts}
                  selectedUserIds={selectedUserIds}
                  onChange={setSelectedUserIds}
                  isOpen={isTeacherFilterOpen}
                  onToggle={handleTeacherFilterToggle}
                />
              </div>
            )}
            {filterConfigs.map((filterConfig) => (
              <div
                key={filterConfig.key}
                className="w-auto"
                data-filter={filterConfig.key}
              >
                <FilterDropdown
                  config={filterConfig}
                  data={data}
                  value={
                    appliedFilters[filterConfig.key as keyof AppliedFilters]
                  }
                  onChange={(value) =>
                    handleFilterChange(
                      filterConfig.key as keyof AppliedFilters,
                      value,
                    )
                  }
                  isOpen={openFilterKey === filterConfig.key}
                  onToggle={() => handleFilterToggle(filterConfig.key)}
                  nameHint={nameHints[filterConfig.key]}
                  includeNotStarted={appliedFilters.includeNotStarted ?? true}
                  onChangeIncludeNotStarted={(val) =>
                    handleFilterChange("includeNotStarted", val)
                  }
                  onApplyBoth={
                    filterConfig.key === Key.LAST_LEARNED
                      ? (value, include) =>
                          handleBatchFilterChange({
                            [Key.LAST_LEARNED]: value as any,
                            includeNotStarted:
                              include === undefined ? true : include,
                          })
                      : undefined
                  }
                />
              </div>
            ))}
          </div>
        </div>
        {isTeacherScopeBlocked && (
          <div
            role="alert"
            className="mx-2 flex flex-row items-center justify-between gap-4 flex-wrap rounded-xl border border-[#F5C2C7] bg-[#FDF2F3] px-4 py-3"
          >
            <p className="font-mulish text-sm text-[#1F2A44]">
              {teacherRequest === "unresolvable"
                ? "We couldn't check which teachers you can view, so this link's analytics weren't loaded. Showing nothing is safer than showing the wrong teacher's data."
                : "You don't have access to the teacher this link points to, so no analytics were loaded."}
            </p>
            <div className="flex flex-row items-center gap-3">
              {teacherRequest === "unresolvable" && (
                <button
                  onClick={() => window.location.reload()}
                  className="font-mulish text-sm font-semibold text-lightgreen underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lightgreen"
                >
                  Try again
                </button>
              )}
              <button
                onClick={() => setSelectedUserIds([])}
                className="font-mulish text-sm font-semibold text-lightgreen underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lightgreen"
              >
                View your own analytics
              </button>
            </div>
          </div>
        )}
        {groupFilterDiagnosis && (
          <div
            role="status"
            className="mx-2 flex flex-row items-center justify-between gap-4 flex-wrap rounded-xl border border-[#F5C2C7] bg-[#FDF2F3] px-4 py-3"
          >
            <p className="font-mulish text-sm text-[#1F2A44]">
              {groupFilterDiagnosis === "malformed"
                ? "That group link isn't valid, so no analytics could be loaded for it."
                : "No analytics available for these filters. This group may not have any activity yet, or you may not have access to it."}
            </p>
            <div className="flex flex-row items-center gap-3">
              <button
                onClick={() =>
                  handleFilterChange(Key.CLASSROOM_NAME, undefined)
                }
                className="font-mulish text-sm font-semibold text-lightgreen underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lightgreen"
              >
                Clear group filter
              </button>
              <button
                onClick={() => navigate(ANALYTICS_PATH)}
                className="font-mulish text-sm font-semibold text-lightgreen underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lightgreen"
              >
                View All Analytics
              </button>
            </div>
          </div>
        )}
        {!isTeacherScopeBlocked && (
          <>
            <HeadingBlock heading="Overview">
              <div
                className="et-analytics-overview"
                data-has-mastery={String(showMastery)}
              >
                <Card
                  label="Total Students"
                  content={
                    isLoading ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-lightgreen/30 border-t-lightgreen [border-radius:50%]" />
                    ) : (
                      <span className="font-mulish text-[#252525] text-2xl font-bold">
                        {filteredTotals.totalStudents}
                      </span>
                    )
                  }
                />
                <Card
                  label="Incomplete Lessons"
                  info="Number of lessons your class has started but have not completed."
                  content={
                    isLoading ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-lightgreen/30 border-t-lightgreen [border-radius:50%]" />
                    ) : (
                      <span className="font-mulish text-[#252525] text-2xl font-bold">
                        {filteredTotals.lessonsStarted -
                          filteredTotals.lessonsCompleted}
                        <span className="text-sm font-medium">
                          {" "}
                          / {filteredTotals.lessonsStarted}
                        </span>
                      </span>
                    )
                  }
                />
                <Card
                  label="Lessons Completed"
                  info="Number of lessons your class has fully completed out of the number they have already started."
                  content={
                    isLoading ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-lightgreen/30 border-t-lightgreen [border-radius:50%]" />
                    ) : (
                      <span className="font-mulish text-[#252525] text-2xl font-bold">
                        {filteredTotals.lessonsCompleted}
                        <span className="text-sm font-medium">
                          {" "}
                          / {filteredTotals.lessonsStarted}
                        </span>
                      </span>
                    )
                  }
                />
                <Card
                  label="Average Time Spent"
                  info="The average time students spend working on each lesson."
                  content={
                    isLoading ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-lightgreen/30 border-t-lightgreen [border-radius:50%]" />
                    ) : (
                      <span className="font-mulish text-[#252525] text-2xl font-bold">
                        {formatMs(filteredTotals.averageTimeSpentMs)}
                      </span>
                    )
                  }
                />
                <Card
                  label="Completion Rate"
                  progress={
                    isLoading ? undefined : filteredTotals.completionRate
                  }
                  info="The average percentage of each lesson your class has completed."
                  content={
                    isLoading ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-lightgreen/30 border-t-lightgreen [border-radius:50%]" />
                    ) : (
                      <span className="font-mulish text-[#252525] text-2xl font-bold">
                        {(filteredTotals.completionRate * 100).toFixed(1)}
                        <small className="et-completion-percent">%</small>
                      </span>
                    )
                  }
                />
                {showMastery && (
                  <Card
                    label="Mastered"
                    info="All exit ticket problems were mastered."
                    content={
                      isLoading ? (
                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-lightgreen/30 border-t-lightgreen [border-radius:50%]" />
                      ) : (
                        <span className="font-mulish text-[#252525] text-2xl font-bold">
                          {filteredTotals.mastered}
                        </span>
                      )
                    }
                  />
                )}
                {showMastery && (
                  <Card
                    label="Not Mastered"
                    info="One or more exit ticket problems were not mastered."
                    content={
                      isLoading ? (
                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-lightgreen/30 border-t-lightgreen [border-radius:50%]" />
                      ) : (
                        <span className="font-mulish text-[#252525] text-2xl font-bold">
                          {filteredTotals.notMastered}
                        </span>
                      )
                    }
                  />
                )}
              </div>
            </HeadingBlock>
            <section
              className="et-analytics-records"
              aria-label="Lesson analytics"
            >
              <HeadingBlock heading="Lesson analytics">
                <div className="et-support-legend">
                  <div className="sr-only">Support levels:</div>
                  {supportKeys.map((metadata) => {
                    const style =
                      displayStateStyleMap[metadata.key as BiteDisplayState] ??
                      LAST_LEARNED_SWATCH;
                    // The legend is a static reference, not a live tile — read
                    // backgroundColor directly, which never carries animation.
                    const legendBackgroundColor = style.backgroundColor;
                    return (
                      <div
                        key={metadata.key}
                        className="et-support-legend-item"
                        data-support={metadata.key}
                      >
                        {metadata.info ? (
                          <div className="group relative flex flex-row items-center justify-center">
                            <Item width={12} height={12}>
                              <div
                                className={`et-support-swatch p-2 rounded-md w-full h-full ${legendBackgroundColor}`}
                              ></div>
                            </Item>
                            <InfoTooltip
                              info={metadata.info}
                              showIcon={false}
                              label={`About ${metadata.label}`}
                              caret={{
                                position: CaretPosition.BOTTOM,
                                color: style.caretColor,
                              }}
                            />
                          </div>
                        ) : (
                          <Item width={12} height={12}>
                            <div
                              className={`et-support-swatch p-2 rounded-md w-full h-full ${legendBackgroundColor}`}
                            ></div>
                          </Item>
                        )}
                        <div>{metadata.label}</div>
                      </div>
                    );
                  })}
                </div>
              </HeadingBlock>
              {isLoading ? (
                <div className="et-analytics-empty-state" role="status">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-lightgreen/30 border-t-lightgreen [border-radius:50%]" />
                  <p className="mt-4 font-mulish text-base font-semibold text-[#252525]">
                    Loading analytics...
                  </p>
                </div>
              ) : visibleGroups.length === 0 && !groupFilterDiagnosis ? (
                <div role="status" className="et-analytics-empty-state">
                  <p className="font-mulish text-base font-semibold text-[#252525]">
                    No analytics available for these filters
                  </p>
                  <p className="mt-2 font-mulish text-sm text-[#25252599]">
                    Try widening the date range or clearing a filter above.
                  </p>
                </div>
              ) : (
                <>
                  <OrgAnnouncementBanner />
                  <AnalyticsTable
                    groups={visibleGroups}
                    headers={displayHeaders}
                    groupBy={groupBy}
                  />
                  <AnalyticsPagination
                    students={filteredTotals.totalStudents}
                    page={currentPage}
                    pages={totalPages}
                    pageSize={pageSizeGroups}
                    sizes={PAGE_SIZE_OPTIONS}
                    onPageChange={goToPage}
                    onPageSizeChange={handlePageSizeChange}
                  />
                </>
              )}
            </section>
          </>
        )}
      </SortProvider>

      {/* Support Transcripts Modal */}
      {transcriptionsEnabled && (
        <SupportTranscriptsModal
          isOpen={supportModal.isOpen}
          onClose={supportModal.closeModal}
          studentName={supportModal.studentName}
          lessonName={supportModal.lessonName}
          biteIndex={supportModal.biteIndex}
          attempt={supportModal.attempt}
          biteType={supportModal.biteType}
        />
      )}

      <ExitTicketDetailModal
        isOpen={detailModal !== null}
        onClose={() => setDetailModal(null)}
        attemptId={detailModal?.attemptId ?? null}
        studentName={detailModal?.studentName ?? ""}
        assessmentLabel={detailModal?.assessmentLabel}
        isDemo={isDemoAccount("sessions")}
      />
    </div>
  );
};

export default AnalyticsViewContent;
